import { LogUtils } from 'galarm-ps-api'
import { GlobalConfig } from 'galarm-config'

const FirebaseManager = (function () {
  let firebaseDb = {}

  const addFirebaseRefChildAdded = function (componentName, ref, callback) {
    addFirebaseRefEventHandler('child_added', componentName, ref, callback)
  }

  const addFirebaseRefChildAddedIfNotPresent = function (
    componentName,
    ref,
    callback
  ) {
    const bindKey = ref.toString()
    try {
      if (
        !firebaseDb[componentName]['firebaseListeners'][bindKey]['child_added']
      ) {
        addFirebaseRefEventHandler('child_added', componentName, ref, callback)
      }
    } catch (error) {
      addFirebaseRefEventHandler('child_added', componentName, ref, callback)
    }
  }

  const addFirebaseRefChildChanged = function (componentName, ref, callback) {
    addFirebaseRefEventHandler('child_changed', componentName, ref, callback)
  }

  const addFirebaseRefChildMoved = function (componentName, ref, callback) {
    addFirebaseRefEventHandler('child_moved', componentName, ref, callback)
  }

  const addFirebaseRefChildRemoved = function (componentName, ref, callback) {
    addFirebaseRefEventHandler('child_removed', componentName, ref, callback)
  }

  const addFirebaseRefValueChanged = function (componentName, ref, callback) {
    addFirebaseRefEventHandler('value', componentName, ref, callback)
  }

  const addFirebaseRefEventHandler = function (
    event,
    componentName,
    ref,
    callback
  ) {
    let unsubscribe

    switch (event) {
      case 'child_added':
        unsubscribe = GlobalConfig.firebaseHelpers.onChildAdded(
          ref,
          callback,
          error => {
            LogUtils.logError(
              error,
              'Unable to bind firebase ' +
                event +
                ' event at location ' +
                ref.toString()
            )
          }
        )
        break
      case 'child_changed':
        unsubscribe = GlobalConfig.firebaseHelpers.onChildChanged(
          ref,
          callback,
          error => {
            LogUtils.logError(
              error,
              'Unable to bind firebase ' +
                event +
                ' event at location ' +
                ref.toString()
            )
          }
        )
        break
      case 'child_moved':
        unsubscribe = GlobalConfig.firebaseHelpers.onChildMoved(
          ref,
          callback,
          error => {
            LogUtils.logError(
              error,
              'Unable to bind firebase ' +
                event +
                ' event at location ' +
                ref.toString()
            )
          }
        )
        break
      case 'child_removed':
        unsubscribe = GlobalConfig.firebaseHelpers.onChildRemoved(
          ref,
          callback,
          error => {
            LogUtils.logError(
              error,
              'Unable to bind firebase ' +
                event +
                ' event at location ' +
                ref.toString()
            )
          }
        )
        break
      case 'value':
        unsubscribe = GlobalConfig.firebaseHelpers.onValue(
          ref,
          callback,
          error => {
            LogUtils.logError(
              error,
              'Unable to bind firebase ' +
                event +
                ' event at location ' +
                ref.toString()
            )
          }
        )
        break
      default:
        console.error('Unsupported Firebase event type:', event)
        return
    }

    addEventHandlerToMap(event, componentName, ref, unsubscribe)
  }

  const addEventHandlerToMap = function (event, componentName, ref, callback) {
    const bindKey = ref.toString()

    if (!firebaseDb[componentName]) {
      firebaseDb[componentName] = {}
      firebaseDb[componentName]['firebaseRefs'] = {}
      firebaseDb[componentName]['firebaseListeners'] = {}
    }

    let firebaseRefs = firebaseDb[componentName]['firebaseRefs']
    let firebaseListeners = firebaseDb[componentName]['firebaseListeners']

    firebaseRefs[bindKey] = ref
    if (!firebaseListeners[bindKey]) {
      firebaseListeners[bindKey] = {}
    }

    // If a listener is already registered for this component/ref/event,
    // unsubscribe it before overwriting its handle. Otherwise the previous
    // listener stays attached on Firebase while its unsubscribe function is
    // lost, leaking an orphan listener that can never be detached. Such
    // orphan listeners survive logout cleanup and then fire permission_denied
    // errors once the auth token is revoked on signOut.
    const existingUnsubscribe = firebaseListeners[bindKey][event]
    if (existingUnsubscribe) {
      existingUnsubscribe()
    }

    firebaseListeners[bindKey][event] = callback
  }

  const unbindFirebaseEventHandlers = function (componentName) {
    if (
      !firebaseDb[componentName] ||
      !firebaseDb[componentName]['firebaseRefs'] ||
      !firebaseDb[componentName]['firebaseListeners']
    ) {
      console.log(
        'No firebase event handlers have been defined for component ' +
          componentName
      )
      return
    }

    const firebaseRefs = firebaseDb[componentName]['firebaseRefs']
    const firebaseListeners = firebaseDb[componentName]['firebaseListeners']

    for (const bindKey in firebaseRefs) {
      if (Object.prototype.hasOwnProperty.call(firebaseRefs, bindKey)) {
        _unbindForKey(bindKey, firebaseRefs, firebaseListeners)
      }
    }
  }

  const unbindFirebaseEventHandlerForChildAdded = function (
    componentName,
    ref
  ) {
    _unbindFirebaseEventHandlerForEvent(componentName, ref, 'child_added')
  }

  const unbindFirebaseEventHandlerForChildRemoved = function (
    componentName,
    ref
  ) {
    _unbindFirebaseEventHandlerForEvent(componentName, ref, 'child_removed')
  }

  const unbindFirebaseEventHandlerForValueChanged = function (
    componentName,
    ref
  ) {
    _unbindFirebaseEventHandlerForEvent(componentName, ref, 'value')
  }

  const _unbindFirebaseEventHandlerForEvent = function (
    componentName,
    ref,
    event
  ) {
    try {
      if (
        !firebaseDb[componentName] ||
        !firebaseDb[componentName]['firebaseRefs'] ||
        !firebaseDb[componentName]['firebaseListeners']
      ) {
        console.log(
          'No firebase event handlers have been defined for component ' +
            componentName
        )
        return
      }
      const bindKey = ref.toString()
      const firebaseRefs = firebaseDb[componentName]['firebaseRefs']
      const firebaseListeners = firebaseDb[componentName]['firebaseListeners']

      if (
        !firebaseRefs[bindKey] ||
        !firebaseListeners[bindKey] ||
        !firebaseListeners[bindKey][event]
      ) {
        console.log(
          'No matching firebase event handlers found for ' +
            componentName +
            '-' +
            bindKey +
            '-' +
            event
        )
        return
      }

      const offListener = firebaseListeners[bindKey][event]
      // In modular API, offListener is the unsubscribe function
      offListener()
      delete firebaseListeners[bindKey][event]
    } catch (error) {
      LogUtils.logError(error, 'Error while unbinding firebase event handler', {
        componentName,
        ref: ref.toString(),
        event
      })
    }
  }

  const _unbindForKey = function (bindKey, firebaseRefs, firebaseListeners) {
    try {
      for (const event in firebaseListeners[bindKey]) {
        if (
          Object.prototype.hasOwnProperty.call(
            firebaseListeners[bindKey],
            event
          )
        ) {
          const offListener = firebaseListeners[bindKey][event]
          // In modular API, offListener is the unsubscribe function
          offListener()
        }
      }
      delete firebaseRefs[bindKey]
      delete firebaseListeners[bindKey]
    } catch (error) {
      LogUtils.logError(error, 'Error while unbinding firebase event handlers')
    }
  }

  return {
    addEventHandlerToMap,
    addFirebaseRefChildAdded,
    addFirebaseRefValueChanged,
    unbindFirebaseEventHandlers,
    addFirebaseRefChildRemoved,
    addFirebaseRefChildChanged,
    addFirebaseRefChildMoved,
    unbindFirebaseEventHandlerForChildAdded,
    unbindFirebaseEventHandlerForChildRemoved,
    unbindFirebaseEventHandlerForValueChanged,
    addFirebaseRefChildAddedIfNotPresent
  }
})()

export default FirebaseManager
